import { useCallback } from 'react'
import { toast } from 'sonner'

import {
	useMutationSendMultipleMessages,
	useMutationUploadChatImage,
} from '../../../api/hooks'
import { useMutationUploadChatMedia } from '../../../pages/ChatSettingsPage/hooks/useMutationUploadChatMedia'
import { createPendingMessage } from '../utils'
import type { PendingMessage } from '../types'
import type { StickerSelection } from '../../StickerPicker'
import type { MediaType } from '../../MediaAttachmentPicker'

import { ChatMessageType, type components } from '@/schema'

type ChatMessageContentDto = components['schemas']['ChatMessageContentDto']
type QuickResponseResponse = components['schemas']['QuickResponseResponse']

interface UseSendMessageOptions {
	roomId: string
	addPendingMessageWithReset: (message: PendingMessage) => boolean
	markPendingAsFailed: (clientId: string) => void
	forceScrollToBottom: () => void
}

function getAudioDuration(file: File): Promise<number> {
	return new Promise((resolve) => {
		const audio = document.createElement('audio')
		audio.preload = 'metadata'

		audio.onloadedmetadata = () => {
			URL.revokeObjectURL(audio.src)
			// Return duration in milliseconds
			resolve(Math.round(audio.duration * 1000))
		}

		audio.onerror = () => {
			URL.revokeObjectURL(audio.src)
			resolve(0)
		}

		audio.src = URL.createObjectURL(file)
	})
}

function getVideoThumbnail(file: File): Promise<Blob | null> {
	return new Promise((resolve) => {
		const video = document.createElement('video')
		video.preload = 'metadata'
		video.muted = true
		video.playsInline = true

		video.onloadeddata = () => {
			video.currentTime = 0.1
		}

		video.onseeked = () => {
			const canvas = document.createElement('canvas')
			canvas.width = video.videoWidth
			canvas.height = video.videoHeight
			const ctx = canvas.getContext('2d')
			if (ctx) {
				ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
				canvas.toBlob(
					(blob) => {
						URL.revokeObjectURL(video.src)
						resolve(blob)
					},
					'image/png',
					0.8,
				)
			} else {
				URL.revokeObjectURL(video.src)
				resolve(null)
			}
		}

		video.onerror = () => {
			URL.revokeObjectURL(video.src)
			resolve(null)
		}

		video.src = URL.createObjectURL(file)
	})
}

export function useSendMessage({
	roomId,
	addPendingMessageWithReset,
	markPendingAsFailed,
	forceScrollToBottom,
}: UseSendMessageOptions) {
	const sendMultipleMessagesMutation = useMutationSendMultipleMessages(roomId)
	const uploadImage = useMutationUploadChatImage()
	const uploadMedia = useMutationUploadChatMedia()

	// Helper to send messages (works for single or multiple)
	const sendMessages = useCallback(
		(
			messages: ChatMessageContentDto[],
			pendingClientIds: string[],
			replyToMessageId?: string,
		) => {
			sendMultipleMessagesMutation.mutate(
				{
					messages,
					replyToMessageId,
				},
				{
					onError: () => {
						// Mark all pending messages as failed
						pendingClientIds.forEach((clientId) =>
							markPendingAsFailed(clientId),
						)
					},
				},
			)
		},
		[sendMultipleMessagesMutation, markPendingAsFailed],
	)

	const sendTextMessage = useCallback(
		(text: string, replyToMessageId?: string) => {
			if (!text.trim()) return

			const clientId = crypto.randomUUID()

			const pendingMessage = createPendingMessage({
				clientId,
				roomId,
				messageType: ChatMessageType.TEXT,
				text,
			})

			// Add pending message - if viewing older messages, this will reset to latest
			const didReset = addPendingMessageWithReset(pendingMessage)
			// If no reset needed, scroll to bottom immediately
			if (!didReset) {
				forceScrollToBottom()
			}

			const messageData: ChatMessageContentDto = {
				clientId,
				messageType: ChatMessageType.TEXT,
				order: 0,
				text,
			}

			sendMessages([messageData], [clientId], replyToMessageId)
		},
		[roomId, addPendingMessageWithReset, forceScrollToBottom, sendMessages],
	)

	const sendQuickResponse = useCallback(
		(response: QuickResponseResponse) => {
			// Sort messages by order
			const sortedMessages = [...response.messages].sort(
				(a, b) => a.order - b.order,
			)

			// Create pending messages for all
			const pendingClientIds: string[] = []

			sortedMessages.forEach((msg) => {
				const clientId = crypto.randomUUID()
				pendingClientIds.push(clientId)

				const pendingMessage = createPendingMessage({
					clientId,
					roomId,
					messageType: msg.messageType as ChatMessageType,
					text: msg.text,
					stickerPackageId: msg.stickerPackageId,
					stickerId: msg.stickerId,
					imagePreviewUrl: msg.imageUrl,
					imageOriginalUrl: msg.imageUrl,
				})

				const didReset = addPendingMessageWithReset(pendingMessage)
				if (!didReset) {
					forceScrollToBottom()
				}
			})

			// Build messages array for API (include clientId for each message)
			const messagesPayload: ChatMessageContentDto[] = sortedMessages.map(
				(msg, index) => ({
					clientId: pendingClientIds[index],
					messageType: msg.messageType as ChatMessageType,
					order: index,
					text: msg.text ?? undefined,
					stickerPackageId: msg.stickerPackageId ?? undefined,
					stickerId: msg.stickerId ?? undefined,
					imageUrl: msg.imageUrl ?? undefined,
					videoUrl: msg.videoUrl ?? undefined,
					videoPreviewUrl: msg.videoPreviewUrl ?? undefined,
					audioUrl: msg.audioUrl ?? undefined,
					audioDuration: msg.audioDuration ?? undefined,
					fileUrl: msg.fileUrl ?? undefined,
					fileName: msg.fileName ?? undefined,
					locationTitle: msg.locationTitle ?? undefined,
					locationAddress: msg.locationAddress ?? undefined,
					locationLatitude: msg.locationLatitude ?? undefined,
					locationLongitude: msg.locationLongitude ?? undefined,
					flexAltText: msg.flexAltText ?? undefined,
					flexContents: msg.flexContents as Record<string, never> | undefined,
				}),
			)

			// Send all messages in one request
			sendMessages(messagesPayload, pendingClientIds)
		},
		[roomId, addPendingMessageWithReset, forceScrollToBottom, sendMessages],
	)

	const sendSticker = useCallback(
		(sticker: StickerSelection) => {
			const clientId = crypto.randomUUID()

			const pendingMessage = createPendingMessage({
				clientId,
				roomId,
				messageType: ChatMessageType.STICKER,
				stickerPackageId: sticker.packageId,
				stickerId: sticker.stickerId,
			})

			const didReset = addPendingMessageWithReset(pendingMessage)
			if (!didReset) {
				forceScrollToBottom()
			}

			const messageData: ChatMessageContentDto = {
				clientId,
				messageType: ChatMessageType.STICKER,
				order: 0,
				stickerPackageId: sticker.packageId,
				stickerId: sticker.stickerId,
			}

			sendMessages([messageData], [clientId])
		},
		[roomId, addPendingMessageWithReset, forceScrollToBottom, sendMessages],
	)

	const sendImage = useCallback(
		async (file: File) => {
			const clientId = crypto.randomUUID()

			// Create a local URL for preview
			const localPreviewUrl = URL.createObjectURL(file)

			const pendingMessage = createPendingMessage({
				clientId,
				roomId,
				messageType: ChatMessageType.IMAGE,
				imagePreviewUrl: localPreviewUrl,
				imageOriginalUrl: localPreviewUrl,
			})

			const didReset = addPendingMessageWithReset(pendingMessage)
			if (!didReset) {
				forceScrollToBottom()
			}

			try {
				// Upload image first
				const result = await uploadImage.mutateAsync(file)

				// Send message with image URL
				const messageData: ChatMessageContentDto = {
					clientId,
					messageType: ChatMessageType.IMAGE,
					order: 0,
					imageUrl: result.fileUrl,
				}

				sendMessages([messageData], [clientId])
			} catch {
				markPendingAsFailed(clientId)
				toast.error('อัพโหลดรูปภาพล้มเหลว กรุณาลองใหม่อีกครั้ง')
			} finally {
				// Clean up local URL
				URL.revokeObjectURL(localPreviewUrl)
			}
		},
		[
			roomId,
			addPendingMessageWithReset,
			forceScrollToBottom,
			uploadImage,
			sendMessages,
			markPendingAsFailed,
		],
	)

	const sendVideo = useCallback(
		async (file: File) => {
			const clientId = crypto.randomUUID()

			// Create a local URL for preview
			const localPreviewUrl = URL.createObjectURL(file)

			const pendingMessage = createPendingMessage({
				clientId,
				roomId,
				messageType: ChatMessageType.VIDEO,
				videoOriginalUrl: localPreviewUrl,
				videoPreviewUrl: localPreviewUrl,
			})

			const didReset = addPendingMessageWithReset(pendingMessage)
			if (!didReset) {
				forceScrollToBottom()
			}

			try {
				// Upload video
				const result = await uploadMedia.mutateAsync({
					file,
					mediaType: 'video',
				})

				// Generate and upload thumbnail
				let thumbnailUrl: string | undefined
				const thumbnailBlob = await getVideoThumbnail(file)
				if (thumbnailBlob) {
					const thumbnailFile = new File([thumbnailBlob], 'thumbnail.png', {
						type: 'image/png',
					})
					const thumbnailResult = await uploadMedia.mutateAsync({
						file: thumbnailFile,
						mediaType: 'image',
					})
					thumbnailUrl = thumbnailResult.fileUrl
				}

				// Send message with video URL
				const messageData: ChatMessageContentDto = {
					clientId,
					messageType: ChatMessageType.VIDEO,
					order: 0,
					videoUrl: result.fileUrl,
					videoPreviewUrl: thumbnailUrl || result.fileUrl,
				}

				sendMessages([messageData], [clientId])
			} catch {
				markPendingAsFailed(clientId)
				toast.error('อัพโหลดวิดีโอล้มเหลว กรุณาลองใหม่อีกครั้ง')
			} finally {
				// Clean up local URL
				URL.revokeObjectURL(localPreviewUrl)
			}
		},
		[
			roomId,
			addPendingMessageWithReset,
			forceScrollToBottom,
			uploadMedia,
			sendMessages,
			markPendingAsFailed,
		],
	)

	const sendAudio = useCallback(
		async (file: File) => {
			const clientId = crypto.randomUUID()

			const pendingMessage = createPendingMessage({
				clientId,
				roomId,
				messageType: ChatMessageType.AUDIO,
				audioOriginalUrl: URL.createObjectURL(file),
			})

			const didReset = addPendingMessageWithReset(pendingMessage)
			if (!didReset) {
				forceScrollToBottom()
			}

			try {
				// Get audio duration
				const duration = await getAudioDuration(file)

				// Upload audio
				const result = await uploadMedia.mutateAsync({
					file,
					mediaType: 'audio',
				})

				// Send message with audio URL
				const messageData: ChatMessageContentDto = {
					clientId,
					messageType: ChatMessageType.AUDIO,
					order: 0,
					audioUrl: result.fileUrl,
					audioDuration: duration,
				}

				sendMessages([messageData], [clientId])
			} catch {
				markPendingAsFailed(clientId)
				toast.error('อัพโหลดเสียงล้มเหลว กรุณาลองใหม่อีกครั้ง')
			}
		},
		[
			roomId,
			addPendingMessageWithReset,
			forceScrollToBottom,
			uploadMedia,
			sendMessages,
			markPendingAsFailed,
		],
	)

	const sendFile = useCallback(
		async (file: File) => {
			const clientId = crypto.randomUUID()

			// Format file size for display
			const formatFileSize = (bytes: number) => {
				if (bytes < 1024) return `${bytes} B`
				if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
				return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
			}

			const pendingMessage = createPendingMessage({
				clientId,
				roomId,
				messageType: ChatMessageType.FLEX,
				text: `📎 ${file.name}`,
			})

			const didReset = addPendingMessageWithReset(pendingMessage)
			if (!didReset) {
				forceScrollToBottom()
			}

			try {
				// Upload file
				const result = await uploadMedia.mutateAsync({
					file,
					mediaType: 'file',
				})

				// Create Flex Message for file download
				// LINE doesn't support FILE type, so we use FLEX with download button
				const flexContents = {
					type: 'bubble',
					body: {
						type: 'box',
						layout: 'vertical',
						contents: [
							{
								type: 'box',
								layout: 'horizontal',
								contents: [
									{
										type: 'text',
										text: '📎',
										size: 'xl',
										flex: 0,
									},
									{
										type: 'box',
										layout: 'vertical',
										contents: [
											{
												type: 'text',
												text: file.name,
												weight: 'bold',
												size: 'sm',
												wrap: true,
												maxLines: 2,
											},
											{
												type: 'text',
												text: formatFileSize(file.size),
												size: 'xs',
												color: '#888888',
											},
										],
										flex: 1,
										margin: 'md',
									},
								],
								alignItems: 'center',
							},
						],
						paddingAll: 'lg',
					},
					footer: {
						type: 'box',
						layout: 'vertical',
						contents: [
							{
								type: 'button',
								action: {
									type: 'uri',
									label: 'ดาวน์โหลด',
									uri: result.fileUrl,
								},
								style: 'primary',
							},
						],
						paddingAll: 'md',
					},
				}

				const messageData: ChatMessageContentDto = {
					clientId,
					messageType: ChatMessageType.FLEX,
					order: 0,
					flexAltText: `📎 ${file.name}`,
					flexContents: flexContents as unknown as Record<string, never>,
				}

				sendMessages([messageData], [clientId])
			} catch {
				markPendingAsFailed(clientId)
				toast.error('อัพโหลดไฟล์ล้มเหลว กรุณาลองใหม่อีกครั้ง')
			}
		},
		[
			roomId,
			addPendingMessageWithReset,
			forceScrollToBottom,
			uploadMedia,
			sendMessages,
			markPendingAsFailed,
		],
	)

	const sendMedia = useCallback(
		async (file: File, mediaType: MediaType) => {
			switch (mediaType) {
				case 'image':
					return sendImage(file)
				case 'video':
					return sendVideo(file)
				case 'audio':
					return sendAudio(file)
				case 'file':
					return sendFile(file)
			}
		},
		[sendImage, sendVideo, sendAudio, sendFile],
	)

	const sendCustomMessage = useCallback(
		(message: ChatMessageContentDto) => {
			const clientId = message.clientId || crypto.randomUUID()

			const pendingMessage = createPendingMessage({
				clientId,
				roomId,
				messageType: message.messageType as ChatMessageType,
				text: message.flexAltText || message.text,
			})

			const didReset = addPendingMessageWithReset(pendingMessage)
			if (!didReset) {
				forceScrollToBottom()
			}

			const messageData: ChatMessageContentDto = {
				...message,
				clientId,
			}

			sendMessages([messageData], [clientId])
		},
		[roomId, addPendingMessageWithReset, forceScrollToBottom, sendMessages],
	)

	return {
		sendTextMessage,
		sendQuickResponse,
		sendSticker,
		sendImage,
		sendVideo,
		sendAudio,
		sendFile,
		sendMedia,
		sendCustomMessage,
		isUploadingMedia: uploadImage.isPending || uploadMedia.isPending,
	}
}
